/**
 * Data Consistency Monitoring Dashboard
 * Epic #17: Data Consistency Monitoring
 *
 * Provides visibility into cross-storage consistency status (D1, KV, R2)
 * Allows platform admins to monitor sync lag, trigger integrity checks, and resolve conflicts
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Grid,
  Column,
  Tile,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Button,
  Loading,
  InlineNotification,
  ProgressBar,
  Modal,
} from '@carbon/react';
import {
  Checkmark,
  Warning,
  MisuseOutline,
  Renew,
  DataCheck,
  DataBase,
  ChartLine,
  TableSplit,
} from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface ConsistencyState {
  operationId: string;
  operationType: 'create' | 'update' | 'delete';
  storageTargets: StorageTarget[];
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'rolled_back';
  priority: 'low' | 'medium' | 'high' | 'critical';
  initiatedAt: string;
  completedAt?: string;
  errorDetails?: string;
  retryCount: number;
  maxRetries: number;
}

interface StorageTarget {
  type: 'D1' | 'KV' | 'R2';
  target: string;
  operation: string;
  data: any;
  status: 'pending' | 'completed' | 'failed';
  checksum?: string;
  version?: string;
  lastAttempt?: string;
  errorMessage?: string;
}

interface SyncState {
  lastSyncTime: string;
  d1LastUpdate: string;
  kvLastUpdate: string;
  r2LastUpdate: string;
  syncLag: number;
  inconsistencyCount: number;
  lastInconsistencyResolved: string;
}

const Consistency: React.FC = () => {
  const { user } = useAuthStore();
  const [token] = useState(() => localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState | null>(null);
  const [operations, setOperations] = useState<ConsistencyState[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [checkRunning, setCheckRunning] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);
  const [showCheckModal, setShowCheckModal] = useState(false);

  // Fetch consistency status and metrics
  const fetchData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setError('Authentication required');
      return;
    }

    try {
      const [statusResponse, metricsResponse, opsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/v1/admin/consistency/status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/v1/admin/consistency/metrics`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/v1/admin/consistency/operations`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (metricsResponse.data) {
        setSyncState(metricsResponse.data);
      }
      if (opsResponse.data) {
        setOperations(Array.isArray(opsResponse.data) ? opsResponse.data : []);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch consistency data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load consistency data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Trigger manual integrity check
  const handleIntegrityCheck = async () => {
    setCheckRunning(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/v1/admin/consistency/check`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh data after check
      await fetchData();
      setShowCheckModal(false);
    } catch (err: any) {
      console.error('Integrity check failed:', err);
      setError(err.response?.data?.message || 'Integrity check failed');
    } finally {
      setCheckRunning(false);
    }
  };

  // Trigger manual sync
  const handleManualSync = async () => {
    setSyncRunning(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/v1/admin/consistency/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh data after sync
      await fetchData();
    } catch (err: any) {
      console.error('Manual sync failed:', err);
      setError(err.response?.data?.message || 'Manual sync failed');
    } finally {
      setSyncRunning(false);
    }
  };

  // Refresh all data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  // Format timestamp
  const formatTimestamp = useCallback((timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  }, []);

  // Format sync lag
  const formatSyncLag = (lag: number) => {
    if (lag < 60) return `${lag}s`;
    if (lag < 3600) return `${Math.floor(lag / 60)}m`;
    return `${Math.floor(lag / 3600)}h`;
  };

  // Operations table headers
  const operationsHeaders = useMemo(
    () => [
      { key: 'operationId', header: 'Operation ID' },
      { key: 'operationType', header: 'Type' },
      { key: 'status', header: 'Status' },
      { key: 'priority', header: 'Priority' },
      { key: 'initiatedAt', header: 'Initiated' },
    ],
    []
  );

  // Operations table rows
  const operationsRows = useMemo(() => {
    return operations.slice(0, 10).map(op => ({
      id: op.operationId,
      operationId: op.operationId.substring(0, 8) + '...',
      operationType: op.operationType,
      status: op.status,
      priority: op.priority,
      initiatedAt: formatTimestamp(op.initiatedAt),
    }));
  }, [operations, formatTimestamp]);

  // Get status tag type
  const getStatusTagType = (status: string): 'green' | 'red' | 'purple' | 'blue' | 'cyan' => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'in_progress':
        return 'purple';
      case 'pending':
        return 'blue';
      default:
        return 'cyan';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <Loading description="Loading consistency monitoring dashboard..." withOverlay={false} />
      </div>
    );
  }

  if (error) {
    return (
      <Grid fullWidth>
        <Column lg={16}>
          <InlineNotification
            kind="error"
            title="Error Loading Dashboard"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        </Column>
      </Grid>
    );
  }

  return (
    <Grid fullWidth style={{ padding: '2rem 0' }}>
      {/* Header Section */}
      <Column lg={16}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 600, marginBottom: '0.5rem' }}>Data Consistency Monitoring</h1>
          <p style={{ color: 'var(--cds-text-secondary)', fontSize: '0.875rem' }}>
            Monitor cross-storage consistency between D1, KV, and R2
          </p>
        </div>
      </Column>

      {/* Metrics Tiles */}
      <Column lg={4} md={4} sm={4}>
        <Tile style={{ padding: '1.5rem', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <ChartLine size={32} />
            <div>
              <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>Sync Lag</p>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 600 }}>
                {syncState ? formatSyncLag(syncState.syncLag) : 'N/A'}
              </h3>
            </div>
          </div>
          <Tag type={syncState && syncState.syncLag < 60 ? 'green' : 'red'} size="sm">
            {syncState && syncState.syncLag < 60 ? 'Healthy' : 'Warning'}
          </Tag>
        </Tile>
      </Column>

      <Column lg={4} md={4} sm={4}>
        <Tile style={{ padding: '1.5rem', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <Warning size={32} />
            <div>
              <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>Inconsistencies</p>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 600 }}>{syncState?.inconsistencyCount || 0}</h3>
            </div>
          </div>
          <Tag type={syncState && syncState.inconsistencyCount === 0 ? 'green' : 'red'} size="sm">
            {syncState && syncState.inconsistencyCount === 0 ? 'Clean' : 'Action Required'}
          </Tag>
        </Tile>
      </Column>

      <Column lg={4} md={4} sm={4}>
        <Tile style={{ padding: '1.5rem', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <DataCheck size={32} />
            <div>
              <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>Operations</p>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 600 }}>{operations.length}</h3>
            </div>
          </div>
          <Tag type="blue" size="sm">
            {operations.filter(op => op.status === 'pending').length} Pending
          </Tag>
        </Tile>
      </Column>

      <Column lg={4} md={4} sm={4}>
        <Tile style={{ padding: '1.5rem', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <TableSplit size={32} />
            <div>
              <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>Last Sync</p>
              <h3 style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                {syncState ? formatTimestamp(syncState.lastSyncTime) : 'N/A'}
              </h3>
            </div>
          </div>
        </Tile>
      </Column>

      {/* Quick Actions */}
      <Column lg={16}>
        <Tile style={{ padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem' }}>Quick Actions</h3>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Button
              kind="primary"
              renderIcon={DataCheck}
              onClick={() => setShowCheckModal(true)}
              disabled={checkRunning}
            >
              {checkRunning ? 'Running Check...' : 'Run Integrity Check'}
            </Button>
            <Button kind="secondary" renderIcon={Renew} onClick={handleManualSync} disabled={syncRunning}>
              {syncRunning ? 'Syncing...' : 'Trigger Manual Sync'}
            </Button>
            <Button kind="ghost" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>
          </div>
        </Tile>
      </Column>

      {/* Storage Layer Status */}
      <Column lg={16}>
        <Tile style={{ padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem' }}>Storage Layer Status</h3>
          <Grid>
            <Column lg={5} md={8} sm={4}>
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 500, marginBottom: '0.5rem' }}>D1 Database</h4>
                <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>
                  Last Update: {syncState ? formatTimestamp(syncState.d1LastUpdate) : 'N/A'}
                </p>
                <ProgressBar value={100} label="Operational" />
              </div>
            </Column>
            <Column lg={5} md={8} sm={4}>
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 500, marginBottom: '0.5rem' }}>KV Store</h4>
                <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>
                  Last Update: {syncState ? formatTimestamp(syncState.kvLastUpdate) : 'N/A'}
                </p>
                <ProgressBar value={100} label="Operational" />
              </div>
            </Column>
            <Column lg={6} md={8} sm={4}>
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 500, marginBottom: '0.5rem' }}>R2 Storage</h4>
                <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>
                  Last Update: {syncState ? formatTimestamp(syncState.r2LastUpdate) : 'N/A'}
                </p>
                <ProgressBar value={100} label="Operational" />
              </div>
            </Column>
          </Grid>
        </Tile>
      </Column>

      {/* Recent Operations */}
      <Column lg={16}>
        <Tile style={{ padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem' }}>Recent Operations</h3>
          {operations.length > 0 ? (
            <DataTable rows={operationsRows} headers={operationsHeaders} size="sm">
              {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                <TableContainer>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow {...getRowProps({ row })} key={row.id}>
                          {row.cells.map(cell => (
                            <TableCell key={cell.id}>
                              {cell.info.header === 'status' ? (
                                <Tag type={getStatusTagType(cell.value as string)} size="sm">
                                  {cell.value}
                                </Tag>
                              ) : cell.info.header === 'priority' ? (
                                <Tag
                                  type={
                                    cell.value === 'critical'
                                      ? 'red'
                                      : cell.value === 'high'
                                        ? 'purple'
                                        : cell.value === 'medium'
                                          ? 'blue'
                                          : 'cyan'
                                  }
                                  size="sm"
                                >
                                  {cell.value}
                                </Tag>
                              ) : (
                                cell.value
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          ) : (
            <InlineNotification
              kind="info"
              title="No Operations"
              subtitle="No consistency operations have been recorded yet"
              lowContrast
              hideCloseButton
            />
          )}
        </Tile>
      </Column>

      {/* Integrity Check Modal */}
      <Modal
        open={showCheckModal}
        onRequestClose={() => setShowCheckModal(false)}
        modalHeading="Run Integrity Check"
        primaryButtonText={checkRunning ? 'Running...' : 'Run Check'}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleIntegrityCheck}
        primaryButtonDisabled={checkRunning}
      >
        <p>
          This will perform a comprehensive integrity check across all storage layers (D1, KV, R2) to identify any
          inconsistencies in your data.
        </p>
        <p style={{ marginTop: '1rem' }}>
          The check may take a few moments to complete. You will see the results in the operations table.
        </p>
      </Modal>
    </Grid>
  );
};

export default Consistency;
